import './VastPreviewBody.scss';

import { Label as TrekLabel } from '@hulu/react-style-components';
import { useFormikContext } from 'formik';
import { bem } from 'mission-control-common-components';
import React from 'react';

import type { IngestionTrial } from '../../../../../apis/graphql';
import { CreativeV5Type } from '../../../../../apis/graphql';
import type { Asset2Extended } from '../../../../../models';
import Alert, { AlertType } from '../../../../Alert';
import { TagInput } from '../../../../Form';
import { DEFAULT_TAG_INPUT_LABEL } from '../../../../Form/TagInput';
import type { AdFormValues } from '../../../adFormik';
import { isCreativeAssetRemoveForbidden } from '../../../utils/creative';
import { useCalculatedCreativeDataByAdCountries } from '../../hooks/useCalculatedCreativeDataByAdCountries';
import useTagUpload from '../../hooks/useTagUpload';
import type { CreativeEditMode, CreativesBuilderValues } from '../../types';
import { creativeTypeToLabel } from '../../utils';
import { VastContent } from '../VastContent';
import { APPROVED_ADS_WARNING_MESSAGE, DEFAULT_TAG_INPUT_LENGTH } from './constants';
import { createVerifiedCreativeWarningMessage } from './utils';
import VastPreviewInput from './VastPreviewInput';

const [block, element] = bem('vast-preview-body');

export type Props = {
  adUuidsWithApprovedVastUrl: string[];
  approvedAdUuids: string[];
  assets?: Asset2Extended[] | null;
  creative: CreativesBuilderValues;
  trialsWithoutAssets?: IngestionTrial[];
  label?: string;
  editMode: CreativeEditMode;
  isURLRemovable?: boolean;
};

export const VastPreviewBody = ({
  assets,
  creative,
  trialsWithoutAssets,
  adUuidsWithApprovedVastUrl,
  approvedAdUuids,
  editMode,
  isURLRemovable,
}: Props): JSX.Element => {
  const {
    values: { lineItem },
  } = useFormikContext<AdFormValues>();

  const { derivedCreativeReviewStatus, derivedCreativeTranscodingStatus } = useCalculatedCreativeDataByAdCountries(
    creative,
    lineItem
  );

  const tagInputProps = useTagUpload();

  const vastOrMagniteLabel = creative.adSourceId ? 'MAGNITE' : creative.type;

  const isTagInputDisabled =
    approvedAdUuids.length > 0 ||
    adUuidsWithApprovedVastUrl.length > 0 ||
    isCreativeAssetRemoveForbidden(editMode, creative);
  const label = assets?.length ? creativeTypeToLabel(vastOrMagniteLabel) : DEFAULT_TAG_INPUT_LABEL;

  const vastPreviewInput =
    tagInputProps.url.length >= DEFAULT_TAG_INPUT_LENGTH ? (
      <VastPreviewInput label={label} isTagInputDisabled={isTagInputDisabled} tagInputProps={tagInputProps} />
    ) : null;

  const hideUrlInput = creative.type === CreativeV5Type.Drax;

  return (
    <div className={block()} data-testid="vast-preview-body">
      {!hideUrlInput && (
        <div className={element('info')}>
          <TagInput
            {...tagInputProps}
            disabled={isTagInputDisabled}
            isURLRemovable={isURLRemovable}
            label={label}
            customInputComponent={vastPreviewInput}
          />
          {approvedAdUuids.length > 0 && <span className={element('message')}>{APPROVED_ADS_WARNING_MESSAGE}</span>}
          {approvedAdUuids.length === 0 && adUuidsWithApprovedVastUrl.length > 0 && (
            <Alert
              type={AlertType.WARNING}
              bodyText={createVerifiedCreativeWarningMessage(adUuidsWithApprovedVastUrl)}
            />
          )}
        </div>
      )}

      <div className={element('content')}>
        <VastContent
          assets={assets}
          creativeName={creative.name}
          creativeType={creative.type}
          trialsWithoutAssets={trialsWithoutAssets}
          creativeId={creative.id}
          derivedCreativeReviewStatus={derivedCreativeReviewStatus}
          derivedCreativeTranscodingStatus={derivedCreativeTranscodingStatus}
        />
        {creative.adSourceId && (
          <>
            <TrekLabel>Ad Source ID</TrekLabel> {creative.adSourceId}
          </>
        )}
      </div>
    </div>
  );
};
